import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faSpinner, faEye, faLinkSlash, faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { useAccountType } from "../hooks/useAccountType";
import { ppapiPost, apiErrorMessage, setViewPartner } from "../utils/partnerApi";

/**
 * Agency → Partners (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.3 D, §7.6).
 * Link an EXISTING partner account by email or wallet: the partner accepts
 * from his own CMS (Security → Agency access). The response is always
 * "request sent" — no account enumeration. Either side can revoke.
 */

const STATUS_PILL = { accepted: "done", pending: "pend", declined: "fail", revoked: "fail" };
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
const isWallet = (s) => /^0x[a-fA-F0-9]{40}$/.test(s);

const AgencyPartners = () => {
  const accountType = useAccountType();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    ppapiPost("agencyListLinks")
      .then((d) => setLinks(Array.isArray(d?.links) ? d.links : []))
      .catch((e) => toast.error(apiErrorMessage(e, "Could not load your partners.")))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (accountType === "agency") load(); }, [accountType, load]);

  const request = () => {
    const v = target.trim();
    if (!isEmail(v) && !isWallet(v)) { toast.error("Enter the partner's account email or a 0x wallet address."); return; }
    setSending(true);
    ppapiPost("agencyRequestLink", isEmail(v) ? { email: v.toLowerCase() } : { wallet: v })
      .then(() => {
        toast.success("Request sent. The partner will see it in his dashboard and can accept it there.");
        setTarget(""); load();
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Could not send the request.")))
      .finally(() => setSending(false));
  };

  const revoke = (l) => {
    if (!window.confirm(`Remove access to ${l.partnerPublicName || l.partnerName}? You can send a new request later.`)) return;
    ppapiPost("revokeAgencyLink", { linkId: l.linkId })
      .then(() => { toast.success("Link removed"); load(); })
      .catch((e) => toast.error(apiErrorMessage(e, "Could not remove the link.")));
  };

  const viewAs = (l) => {
    setViewPartner({ id: l.partnerId, name: l.partnerPublicName || l.partnerName });
    navigate("/overview");
  };

  if (accountType !== "agency") {
    return (
      <div className="acWrap"><div className="acCard" style={{ maxWidth: 560 }}>
        <div className="acName">Agency tools</div>
        <p className="secLead">This section is for agency accounts. Reach out on Discord if you run an agency.</p>
        <Link to="/overview" className="btnGhost">Back to dashboard</Link>
      </div></div>
    );
  }

  const cols = "minmax(180px,1.4fr) 110px 110px 130px 120px";

  return (
    <>
      <Helmet><title>Agency Partners | Instaswap Partners</title></Helmet>
      <div>
        <div className="secHdr">
          <h2 className="secTitle">Partners</h2>
          <p className="secLead">
            The KOL accounts you monitor. Link an existing account by its email or wallet — the partner approves from his own
            dashboard. New KOLs without an account? Send them an <Link to="/agency/invites" style={{ color: "#3ad17e" }}>invite link</Link> instead.
          </p>
        </div>

        <div className="panel" style={{ maxWidth: 900, marginBottom: 24, padding: 18 }}>
          <h3 className="secH" style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <FontAwesomeIcon icon={faUserPlus} /> Link a partner account
          </h3>
          <p className="fldHelp" style={{ margin: "0 0 12px" }}>
            Account email or wallet address of the partner. You will only see his data after he accepts.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              className="input" type="text" value={target} placeholder="partner@email.com or 0x…" style={{ flex: 1, minWidth: 260 }}
              onChange={(e) => setTarget(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !sending) request(); }}
            />
            <button type="button" className="btnPrimary" onClick={request} disabled={sending || !target.trim()}>
              {sending ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faPaperPlane} /> Send request</>}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="gtEmpty"><FontAwesomeIcon icon={faSpinner} spin /> Loading…</div>
        ) : links.length === 0 ? (
          <div className="gtEmpty">No linked partners yet.</div>
        ) : (
          <div className="gtScroll">
            <div style={{ minWidth: 760 }}>
              <div className="gtHead" style={{ gridTemplateColumns: cols }}>
                <span>Partner</span><span>Status</span><span>Source</span><span>Since</span><span className="r">Actions</span>
              </div>
              {links.map((l) => {
                const st = String(l.status || "pending").toLowerCase();
                return (
                  <div className="gtRow" key={l.linkId} style={{ gridTemplateColumns: cols }}>
                    <span className="cellStrong">
                      {l.partnerPublicName || l.partnerName || `Partner #${l.partnerId}`}
                      {l.partnerEmailMasked && <small style={{ display: "block", color: "#7c847e" }}>{l.partnerEmailMasked}</small>}
                    </span>
                    <span><span className={`pill ${STATUS_PILL[st] || "pend"}`}><span className="pd" />{st}</span></span>
                    <span style={{ color: "#aeb6b0", textTransform: "capitalize" }}>{l.source || "request"}</span>
                    <span>{(l.decidedAt || l.requestedAt) ? new Date(l.decidedAt || l.requestedAt).toLocaleDateString() : "—"}</span>
                    <span className="r" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      {st === "accepted" && (
                        <button type="button" className="iconBtn edit" title="View as this partner" onClick={() => viewAs(l)}><FontAwesomeIcon icon={faEye} /></button>
                      )}
                      {(st === "accepted" || st === "pending") && (
                        <button type="button" className="iconBtn danger" title={st === "pending" ? "Cancel request" : "Remove access"} onClick={() => revoke(l)}><FontAwesomeIcon icon={faLinkSlash} /></button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AgencyPartners;
